import React, { useEffect, useRef, useState } from "react";
import {
  AlertDialog,
  AlertDialogBody,
  AlertDialogContent,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogOverlay,
  Button,
  Flex,
  FormControl,
  FormHelperText,
  Input,
  Select,
  Text,
  Textarea,
  VStack,
} from "@chakra-ui/react";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { reportBug } from "../../store/user/thunks";
import useToastNotification from "../../hooks/useToastNotification";
import { FEEDBACK_PAGE_OPTIONS } from "../../data/pages";

// Shared Submit-Feedback dialog used by both the Navbar (global feedback button)
// and the My Feedback page (Submit New Feedback button). Persists in-flight form
// state to localStorage so a refresh doesn't lose what the user typed.
export default function SubmitFeedbackDialog({
  isOpen,
  onClose,
  showDeepLink = true,
  onSubmitSuccess,
}) {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const showToast = useToastNotification();
  const cancelRef = useRef();

  const [requestType, setRequestType] = useState("");
  const [page, setPage] = useState("");
  const [summary, setSummary] = useState("");
  const [description, setDescription] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    const saved = window.localStorage.getItem("feedbackInfo");
    if (saved) {
      try {
        const info = JSON.parse(saved);
        setRequestType(info.requestType || "");
        setPage(info.page || "");
        setDescription(info.description || "");
        setSummary(info.summary || "");
      } catch (e) {
        // ignore corrupt storage
      }
    }
  }, []);

  const clearForm = () => {
    setRequestType("");
    setPage("");
    setDescription("");
    setSummary("");
    window.localStorage.removeItem("feedbackInfo");
  };

  const handleConfirm = async (e) => {
    e.preventDefault();
    const feedbackInfo = { requestType, summary, description, page };
    window.localStorage.setItem("feedbackInfo", JSON.stringify(feedbackInfo));
    setIsSubmitting(true);
    try {
      const result = await dispatch(reportBug(feedbackInfo)).unwrap();
      clearForm();
      onClose();
      showToast(result?.result || "Feedback Submitted Successfully");
      if (onSubmitSuccess) onSubmitSuccess(result);
    } catch (err) {
      showToast(err?.response?.data?.result || "Failed to submit feedback", {
        variant: "solid",
        status: "error",
        duration: 10000,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCancel = () => {
    clearForm();
    onClose();
  };

  const handleClear = () => {
    clearForm();
  };

  const handleViewMyFeedback = () => {
    handleCancel();
    navigate("/myfeedback");
  };

  return (
    <AlertDialog
      motionPreset="slideInBottom"
      isOpen={isOpen}
      leastDestructiveRef={cancelRef}
      onClose={handleCancel}
      isCentered
      closeOnOverlayClick={false}
    >
      <AlertDialogOverlay>
        <AlertDialogContent>
          <AlertDialogHeader fontSize="lg" fontWeight="bold" pb={1}>
            <Flex justifyContent="space-between" alignItems="center" width="100%">
              <Text>Provide Feedback</Text>
              <Button
                size="sm"
                variant="ghost"
                colorScheme="gray"
                borderRadius="md"
                onClick={handleClear}
                isDisabled={isSubmitting}
              >
                Clear
              </Button>
            </Flex>
          </AlertDialogHeader>

          <AlertDialogBody pt={1}>
            <VStack spacing={2} align="stretch">
              <FormControl>
                <FormHelperText mb={1} ml={1}>Feedback Type *</FormHelperText>
                <Select
                  size="sm"
                  value={requestType}
                  placeholder="Bug Report or Feature Request?"
                  onChange={(e) => setRequestType(e.target.value)}
                >
                  <option>Bug Report</option>
                  <option>Feature Request</option>
                </Select>
              </FormControl>
              <FormControl>
                <FormHelperText mb={1} ml={1}>Summary *</FormHelperText>
                <Input
                  size="sm"
                  type="name"
                  value={summary}
                  onChange={(e) => setSummary(e.target.value)}
                />
              </FormControl>
              <FormControl>
                <FormHelperText mb={1} ml={1}>Page *</FormHelperText>
                <Select
                  size="sm"
                  value={page}
                  placeholder="Which Page is affected?"
                  onChange={(e) => setPage(e.target.value)}
                >
                  {FEEDBACK_PAGE_OPTIONS.map((opt) => (
                    <option key={opt}>{opt}</option>
                  ))}
                </Select>
              </FormControl>
              <FormControl>
                <FormHelperText mb={1} ml={1}>Description *</FormHelperText>
                <Textarea
                  size="sm"
                  value={description}
                  placeholder="Describe the bug you are experiencing..."
                  onChange={(e) => setDescription(e.target.value)}
                />
              </FormControl>
            </VStack>
          </AlertDialogBody>

          <AlertDialogFooter paddingTop={4} flexDirection="column" alignItems="stretch" gap={4}>
            <Flex justifyContent="flex-end">
              <Button
                ref={cancelRef}
                borderRadius="md"
                onClick={handleCancel}
                isDisabled={isSubmitting}
              >
                Cancel
              </Button>
              <Button
                colorScheme="blue"
                borderRadius="md"
                onClick={handleConfirm}
                ml={3}
                isLoading={isSubmitting}
                loadingText="Submitting"
              >
                Submit
              </Button>
            </Flex>
            {showDeepLink && (
              <Button
                variant="link"
                size="sm"
                colorScheme="blue"
                onClick={handleViewMyFeedback}
                isDisabled={isSubmitting}
                mt={2}
              >
                View My Feedback →
              </Button>
            )}
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialogOverlay>
    </AlertDialog>
  );
}
